import React, { useState, useEffect } from 'react';
import {
  Card,
  Select,
  Table,
  Typography,
  Space,
  Button,
  message,
  Modal,
} from 'antd';
import { getDirectusClient } from '../../lib/directus';
import {
  readUsers,
  readItems,
  createItem,
  updateItem,
  deleteItem,
} from '@directus/sdk';
import Layouts from '../../components/Layouts';
import FacturaDetailsModal from './components/FacturaDetailsModal';

import moment from 'moment';

import ConsignacionModal from '../liquidaciones/components/ConsignacionModal';
import NovedadModal from '../liquidaciones/components/NovedadModal';
import VendorDetails from '../liquidaciones/components/VendorDetails';
import { useSelector } from 'react-redux';
import { formatPrice } from '../../lib/utils';

const { Option } = Select;
const { Title } = Typography;

function LiquidacionesVendedor() {
  const [vendedors, setvendedors] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedvendedor, setSelectedvendedor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allCajasLiquidadas, setAllCajasLiquidadas] = useState(false);
  const signoMoneda = useSelector(state => state.signoMoneda.signo_moneda);
  const [isDetailsVisible, setIsDetailsVisible] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);

  // Add new states after existing useState declarations
  const [hasActiveLiquidacion, setHasActiveLiquidacion] = useState(false);
  const [currentLiquidacionId, setCurrentLiquidacionId] = useState(null);
  const [isConsignacionModalVisible, setIsConsignacionModalVisible] =
    useState(false);

  // Add new state for novedad modal
  const [novedadModalVisible, setNovedadModalVisible] = useState(false);
  const [novedad, setNovedad] = useState('');
  const [diferencia, setDiferencia] = useState(0);

  // Add a new state to store totals
  const [totals, setTotals] = useState({
    total_facturas: 0,
    total_credito: 0,
    total_devoluciones: 0,
  });

  // New state for facturas
  const [facturas, setFacturas] = useState([]);

  // Add after existing state declarations
  const [consignaciones, setConsignaciones] = useState([]);

  // Add state for factura details modal
  const [isFacturaDetailsVisible, setIsFacturaDetailsVisible] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState(null);

  // First, modify fetchvendedors to fetch vendedores instead
  const fetchVendedores = async () => {
    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readUsers({
          fields: ['id', 'first_name', 'last_name', 'role.*'],
          filter: {
            role: { name: { _contains: 'vendedor' } }, // Changed from vendedor to vendedor
          },
        })
      );
      setvendedors(response); // We'll keep the state name but it contains vendedores
    } catch (error) {
      console.error('Error fetching vendedores:', error);
    }
  };

  // Modify fetchVendors to fetch cajas for a specific vendedor
  const fetchVendorCajas = async (vendedorId, liquidacionId = null) => {
    if (!vendedorId) {
      // Clear data if no vendedor is selected
      setFacturas([]);
      setTotals({
        total_facturas: 0,
        total_credito: 0,
        total_devoluciones: 0,
      });
      return;
    }

    setLoading(true);
    try {
      const directus = await getDirectusClient();

      let cajasIds = [];

      if (liquidacionId) {
        // If there's an active liquidacion, get cajas from that liquidacion
        const liquidacion = await directus.request(
          readItems('liquidacion', {
            fields: ['id', 'cajas.*', 'responsable'],
            filter: { id: { _eq: liquidacionId } },
          })
        );

        // Validate that liquidacion exists and belongs to the correct vendedor
        if (
          liquidacion?.length > 0 &&
          liquidacion[0].responsable === vendedorId
        ) {
          // Extract cajas_id from liquidacion.cajas array
          cajasIds = liquidacion[0].cajas.map(caja => caja.cajas_id);
        } else {
          // Clear data if liquidacion doesn't exist or doesn't belong to this vendedor
          setFacturas([]);
          setTotals({
            total_facturas: 0,
            total_credito: 0,
            total_devoluciones: 0,
          });
          setLoading(false);
          return;
        }
      } else {
        // Get all unliquidated cajas for this vendedor
        const cajas = await directus.request(
          readItems('cajas', {
            fields: ['id', 'liquidada', 'estado'],
            filter: {
              _and: [
                { liquidada: { _eq: false } },
                { vendedor: { _eq: vendedorId } },
                { estado: { _eq: 2 } }, // Solo cajas cerradas
              ],
            },
          })
        );

        cajasIds = cajas.map(caja => caja.id);
      }

      // Rest of the logic remains similar, just fetch facturas for these cajas
      if (!cajasIds || cajasIds.length === 0) {
        setFacturas([]); // Changed from setVendors([])
        setTotals({
          total_facturas: 0,
          total_credito: 0,
          total_devoluciones: 0,
        });
        setLoading(false);
        return;
      }

      // Fetch facturas for these cajas
      const facturas = await directus.request(
        readItems('facturas', {
          fields: [
            'id',
            'caja.id',
            'productos.cantidad',
            'productos.precio_unitario',
            'productos.producto.id',
            'productos.producto.nombre',
            'productos.producto.grupo.impuesto',
            'estado',
            'tipo',
            'consecutivo',
            'date_created',
          ],
          filter: {
            caja: { _in: cajasIds },
          },
        })
      );

      // Calculate totals
      const calculatedTotals = facturas.reduce(
        (acc, factura) => {
          const total = (factura.productos || []).reduce((sum, prod) => {
            const subtotal = (prod.cantidad || 0) * (prod.precio_unitario || 0);
            const impuesto = prod.producto?.grupo?.impuesto || 0;
            const impuestoValue = subtotal * (impuesto / 100);
            return sum + (subtotal + impuestoValue);
          }, 0);

          if (factura.tipo === 1) {
            if (factura.estado === 'creada') {
              acc.total_facturas += total;
            } else if (factura.estado === 'devolucion') {
              acc.total_devoluciones += total;
            }
          } else if (factura.tipo === 2) {
            acc.total_credito += total;
          }
          return acc;
        },
        { total_facturas: 0, total_credito: 0, total_devoluciones: 0 }
      );

      setTotals(calculatedTotals);
      setFacturas(facturas); // New state for facturas
    } catch (error) {
      console.error('Error fetching vendor data:', error);
      message.error('Error al cargar los datos');
      // Clear data on error
      setFacturas([]);
      setTotals({
        total_facturas: 0,
        total_credito: 0,
        total_devoluciones: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  // Add new function to check for active liquidacion
  const checkActiveLiquidacion = async vendedorId => {
    if (!vendedorId) return;

    try {
      const directus = await getDirectusClient();
      const today = moment().startOf('day');
      const tomorrow = moment().endOf('day');

      // First check if there's an active liquidacion for today
      const liquidacion = await directus.request(
        readItems('liquidacion', {
          fields: ['id', 'estado', 'cajas.*'],
          filter: {
            _and: [
              { responsable: { _eq: vendedorId } },
              { estado: { _eq: 'incompleta' } },
            ],
          },
          limit: 1,
        })
      );

      // If there's no liquidacion, check for unliquidated cajas
      if (!liquidacion?.length) {
        const cajas = await directus.request(
          readItems('cajas', {
            fields: ['*.*', 'id', 'liquidada', 'estado', 'cajas.*'],
            filter: {
              _and: [
                { vendedor: { _eq: vendedorId } },
                { estado: { id: { _eq: 2 } } }, // Solo cajas cerradas
                { liquidada: { _eq: false } },
              ],
            },
          })
        );

        setHasActiveLiquidacion(false);
        setCurrentLiquidacionId(null);
        setAllCajasLiquidadas(cajas.length === 0);

        if (cajas.length > 0) {
          fetchVendorCajas(vendedorId); // Changed from fetchVendors
        } else {
          // Clear data when no cajas are available
          setFacturas([]);
          setTotals({
            total_facturas: 0,
            total_credito: 0,
            total_devoluciones: 0,
          });
        }
        return;
      }

      // If there is a liquidacion, check its cajas status
      const activeLiquidacion = liquidacion[0];

      console.log('liquidacion activa', activeLiquidacion.cajas[0]);

      // Get status of cajas in this liquidacion
      const liquidacionCajas = await directus.request(
        readItems('cajas', {
          fields: ['id', 'liquidada', 'estado'],
          filter: {
            _and: [
              { id: { _in: activeLiquidacion.cajas[0].cajas_id } },
              { estado: { id: { _eq: 2 } } }, // Solo cajas cerradas
            ],
          },
        })
      );

      // Check if all cajas in liquidacion are liquidated
      const allCajasAreLiquidated = liquidacionCajas.every(
        caja => caja.liquidada
      );

      setCurrentLiquidacionId(activeLiquidacion.id);
      setHasActiveLiquidacion(!allCajasAreLiquidated);
      setAllCajasLiquidadas(allCajasAreLiquidated);

      // Only fetch vendors if there are unliquidated cajas
      if (!allCajasAreLiquidated) {
        fetchVendorCajas(vendedorId, activeLiquidacion.id); // Pass liquidacion ID
      } else {
        setVendors([]);
        setFacturas([]);
        setTotals({
          total_facturas: 0,
          total_credito: 0,
          total_devoluciones: 0,
        });
      }
    } catch (error) {
      console.error('Error checking active liquidacion:', error);
      message.error('Error al verificar el estado de la liquidación');
    }
  };

  // Add function to create liquidacion
  const handleCreateLiquidacion = async () => {
    try {
      const directus = await getDirectusClient();
      const today = moment().startOf('day');
      const tomorrow = moment().endOf('day');

      // Get all cajas for vendors under this vendedor
      const cajas = await directus.request(
        readItems('cajas', {
          fields: [
            'id',
            'total_ventas',
            'facturas.tipo',
            'facturas.estado',
            'facturas.productos.cantidad',
            'facturas.productos.precio_unitario',
            'facturas.productos.producto.grupo.impuesto',
          ],
          filter: {
            _and: [
              { liquidada: { _eq: false } },
              { vendedor: { _eq: selectedvendedor } },
            ],
          },
        })
      );

      // Validar si hay cajas
      if (!cajas || cajas.length === 0) {
        message.info('No hay cajas pendientes por liquidar para este vendedor');
        return;
      }

      // Get total of consignaciones for these cajas
      const consignaciones = await directus.request(
        readItems('consignaciones', {
          fields: ['monto'],
          filter: {
            _and: [
              {
                liquidacion: { id: { _eq: currentLiquidacionId } },
              },
              {
                date_created: {
                  _between: [today.toISOString(), tomorrow.toISOString()],
                },
              },
            ],
          },
        })
      );

      const totalConsignaciones = consignaciones.reduce(
        (sum, cons) => sum + parseFloat(cons.monto || 0),
        0
      );

      // Calculate totals from facturas
      const totals = cajas.reduce(
        (acc, caja) => {
          const facturas = caja.facturas || [];

          facturas.forEach(factura => {
            const total = (factura.productos || []).reduce((sum, prod) => {
              const subtotal =
                (prod.cantidad || 0) * (prod.precio_unitario || 0);
              const impuesto = prod.producto?.grupo?.impuesto || 0;
              const impuestoValue = subtotal * (impuesto / 100);
              return sum + (subtotal + impuestoValue);
            }, 0);

            if (factura.tipo === 1) {
              if (factura.estado === 'creada') {
                acc.total_facturas += total;
              } else if (factura.estado === 'devolucion') {
                acc.total_devoluciones += total;
              }
            } else if (factura.tipo === 2) {
              acc.total_credito += total;
            }
          });

          return acc;
        },
        { total_facturas: 0, total_credito: 0, total_devoluciones: 0 }
      );

      // Calculate diferencia
      const diferencia = totalConsignaciones - totals.total_facturas;
      const requiereNovedad = Math.abs(diferencia) > 0;

      // Create liquidacion with totals
      const liquidacion = await directus.request(
        createItem('liquidacion', {
          ...totals,
          responsable: selectedvendedor,
          estado: 'incompleta',
          tipo: 'rutas',
          cajas: {
            create: cajas.map(caja => ({
              liquidacion_id: '+',
              cajas_id: {
                id: caja.id,
              },
            })),
            update: [],
            delete: [],
          },
          diferencia: diferencia,
        })
      );

      setCurrentLiquidacionId(liquidacion.id);
      setHasActiveLiquidacion(true);

      if (requiereNovedad) {
        message.warning(
          'Se detectó una diferencia. Por favor registre una novedad.'
        );
      } else {
        message.success('Liquidación creada exitosamente');
      }
    } catch (error) {
      console.error('Error creating liquidacion:', error);
      message.error('Error al crear la liquidación');
    }
  };
  console.log(totals);
  const handleFinalizeLiquidacion = async () => {
    if (!currentLiquidacionId) return;

    try {
      const directus = await getDirectusClient();
      const today = moment().startOf('day');
      const tomorrow = moment().endOf('day');

      // Get all cajas linked to this liquidacion
      const liquidacion = await directus.request(
        readItems('liquidacion', {
          fields: ['id', 'total_facturas', 'cajas.*'],
          filter: { id: { _eq: currentLiquidacionId } },
        })
      );

      if (!liquidacion?.length) {
        throw new Error('Liquidación no encontrada');
      }

      // Get consignaciones for these cajas
      const consignaciones = await directus.request(
        readItems('consignaciones', {
          fields: ['monto'],
          filter: {
            _and: [
              {
                liquidacion: { id: { _eq: currentLiquidacionId } },
              },
              {
                date_created: {
                  _between: [today.toISOString(), tomorrow.toISOString()],
                },
              },
            ],
          },
        })
      );

      const totalConsignaciones = consignaciones.reduce(
        (sum, cons) => sum + parseFloat(cons.monto || 0),
        0
      );

      // Validar si hay consignaciones
      if (totalConsignaciones === 0) {
        message.error('No hay consignaciones registradas');
        return;
      }

      // Calculate diferencia
      const calculatedDiferencia = totalConsignaciones - totals.total_facturas;

      if (Math.abs(calculatedDiferencia) > 0) {
        setDiferencia(calculatedDiferencia);
        setNovedadModalVisible(true);
        return;
      }

      await finalizeLiquidacion(calculatedDiferencia);
    } catch (error) {
      console.error('Error finalizing liquidacion:', error);
      message.error('Error al finalizar la liquidación');
    }
  };

  // Add function to actually finalize liquidacion
  const finalizeLiquidacion = async (
    calculatedDiferencia,
    novedadText = null
  ) => {
    try {
      const directus = await getDirectusClient();

      // First get the cajas IDs from the liquidacion
      const liquidacion = await directus.request(
        readItems('liquidacion', {
          fields: ['id', 'cajas.*'],
          filter: { id: { _eq: currentLiquidacionId } },
        })
      );

      if (!liquidacion?.length) {
        throw new Error('Liquidación no encontrada');
      }

      // Update liquidacion
      await directus.request(
        updateItem('liquidacion', currentLiquidacionId, {
          estado: 'creada',
          diferencia: calculatedDiferencia,
          novedades: novedadText,
          total_facturas: totals.total_facturas,
          total_credito: totals.total_credito,
          total_devoluciones: totals.total_devoluciones,
        })
      );

      console.log('cajas liquidacion', liquidacion);

      const cajasUpdates = liquidacion[0].cajas.map(cajaObj =>
        directus.request(
          updateItem('cajas', cajaObj.cajas_id, {
            liquidada: true,
          })
        )
      );

      await Promise.all(cajasUpdates);

      setHasActiveLiquidacion(false);
      setCurrentLiquidacionId(null);
      setNovedadModalVisible(false);
      setNovedad('');
      message.success('Liquidación finalizada exitosamente');

      // Refresh vendors list to reflect updated cajas
      fetchVendorCajas(selectedvendedor, currentLiquidacionId);
    } catch (error) {
      console.error('Error updating liquidacion:', error);
      message.error('Error al actualizar la liquidación');
    }
  };

  // Add after existing functions
  const fetchConsignaciones = async liquidacionId => {
    if (!liquidacionId) return;

    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readItems('consignaciones', {
          fields: [
            'id',
            'monto',
            'date_created',
            'user_created.first_name',
            'user_created.last_name',
            'tipo',
            'banco.nombre',
            'referencia',
            'nombre',
          ],
          filter: {
            liquidacion: { id: { _eq: liquidacionId } },
          },
          sort: ['-date_created'],
        })
      );
      setConsignaciones(response);
    } catch (error) {
      console.error('Error fetching consignaciones:', error);
      message.error('Error al cargar las consignaciones');
    }
  };

  // Add handleDeleteConsignacion function
  const handleDeleteConsignacion = async consignacionId => {
    try {
      const directus = await getDirectusClient();
      await directus.request(deleteItem('consignaciones', consignacionId));

      message.success('Consignación eliminada correctamente');
      fetchConsignaciones(currentLiquidacionId);
      fetchVendorCajas(selectedvendedor, currentLiquidacionId);
    } catch (error) {
      console.error('Error deleting consignacion:', error);
      message.error('Error al eliminar la consignación');
    }
  };

  useEffect(() => {
    fetchVendedores();
  }, []);

  // Update the useEffect for selectedvendedor
  useEffect(() => {
    if (selectedvendedor) {
      // Don't call fetchVendorCajas here, let checkActiveLiquidacion handle it
      checkActiveLiquidacion(selectedvendedor);
      if (currentLiquidacionId) {
        fetchConsignaciones(currentLiquidacionId);
      }
    } else {
      setFacturas([]);
      setConsignaciones([]);
      setHasActiveLiquidacion(false);
      setCurrentLiquidacionId(null);
    }
  }, [selectedvendedor, currentLiquidacionId]);

  // Modify the columns for facturas table
  const columns = [
    {
      title: 'Consecutivo',
      dataIndex: 'consecutivo',
      key: 'consecutivo',
    },
    {
      title: 'Caja',
      dataIndex: 'caja',
      key: 'caja',
      render: caja => caja?.id || '-',
    },
    {
      title: 'Fecha',
      dataIndex: 'date_created',
      key: 'date_created',
      render: value => moment(value).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Total',
      key: 'total',
      render: (_, record) => {
        const total = (record.productos || []).reduce((sum, prod) => {
          const subtotal = (prod.cantidad || 0) * (prod.precio_unitario || 0);
          const impuesto = prod.producto?.grupo?.impuesto || 0;
          const impuestoValue = subtotal * (impuesto / 100);
          return sum + (subtotal + impuestoValue);
        }, 0);
        return formatPrice(total, signoMoneda);
      },
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            setSelectedFactura(record);
            setIsFacturaDetailsVisible(true);
          }}
        >
          Ver detalles
        </Button>
      ),
    },
  ];

  // Add after existing columns definition
  const consignacionesColumns = [
    {
      title: 'Fecha',
      dataIndex: 'date_created',
      key: 'date_created',
      render: date => moment(date).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
    },
    {
      title: 'Banco',
      dataIndex: 'banco',
      key: 'banco',
      render: banco => banco?.nombre || '-',
    },
    {
      title: 'Referencia',
      dataIndex: 'referencia',
      key: 'referencia',
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: 'Monto',
      dataIndex: 'monto',
      key: 'monto',
      render: value => formatPrice(value, signoMoneda),
    },
    {
      title: 'Creado por',
      key: 'user_created',
      render: (_, record) =>
        `${record.user_created?.first_name || ''} ${
          record.user_created?.last_name || ''
        }`,
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          danger
          onClick={() => {
            Modal.confirm({
              title: '¿Está seguro de eliminar esta consignación?',
              content: 'Esta acción no se puede deshacer',
              okText: 'Sí, eliminar',
              cancelText: 'No, cancelar',
              onOk: () => handleDeleteConsignacion(record.id),
            });
          }}
        >
          Eliminar
        </Button>
      ),
    },
  ];

  return (
    <Layouts>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space direction="horizontal" style={{ marginBottom: 16 }}>
            <Title level={4}>Liquidaciones por Vendedor</Title>
            <Button
              type="primary"
              onClick={handleCreateLiquidacion}
              disabled={
                !selectedvendedor || hasActiveLiquidacion || allCajasLiquidadas
              }
            >
              Crear Liquidación
            </Button>
            {hasActiveLiquidacion && !allCajasLiquidadas && (
              <>
                <Button
                  onClick={() => setIsConsignacionModalVisible(true)}
                  disabled={!selectedvendedor || !currentLiquidacionId}
                >
                  Crear Consignación
                </Button>
                <Button
                  type="primary"
                  danger
                  onClick={handleFinalizeLiquidacion}
                  disabled={!currentLiquidacionId}
                >
                  Finalizar Liquidación
                </Button>
              </>
            )}
          </Space>

          <Select
            style={{ width: 300 }}
            placeholder="Seleccione un vendedor"
            onChange={setSelectedvendedor} // We'll keep the handler name but it's for vendedor
            allowClear
          >
            {vendedors.map(vendedor => (
              <Option key={vendedor.id} value={vendedor.id}>
                {`${vendedor.first_name} ${vendedor.last_name}`}
              </Option>
            ))}
          </Select>

          <Table
            columns={columns}
            dataSource={facturas}
            loading={loading}
            rowKey="id"
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <strong>Total General</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} />
                <Table.Summary.Cell index={2} />
                <Table.Summary.Cell index={3}>
                  <strong>
                    {formatPrice(totals.total_facturas, signoMoneda)}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} />
                <Table.Summary.Cell index={5} />
              </Table.Summary.Row>
            )}
          />
          {hasActiveLiquidacion && (
            <>
              <Title level={5} style={{ marginTop: 16 }}>
                Consignaciones
              </Title>
              <Table
                columns={consignacionesColumns}
                dataSource={consignaciones}
                loading={loading}
                rowKey="id"
                pagination={false}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={5}>
                      <strong>Total Consignaciones</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>
                      <strong>
                        {formatPrice(
                          consignaciones.reduce(
                            (sum, cons) => sum + parseFloat(cons.monto || 0),
                            0
                          ),
                          signoMoneda
                        )}
                      </strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} />
                    <Table.Summary.Cell index={7} />
                  </Table.Summary.Row>
                )}
              />
            </>
          )}
        </Space>
      </Card>
      <VendorDetails
        visible={isDetailsVisible}
        vendor={selectedVendor}
        onClose={() => {
          setIsDetailsVisible(false);
          setSelectedVendor(null);
        }}
        cajasIds={selectedVendor?.cajas}
      />

      <ConsignacionModal
        visible={isConsignacionModalVisible}
        liquidacionId={currentLiquidacionId}
        onClose={() => setIsConsignacionModalVisible(false)}
        onSuccess={() => {
          setIsConsignacionModalVisible(false);
          fetchVendorCajas(selectedvendedor, currentLiquidacionId);
          fetchConsignaciones(currentLiquidacionId);
        }}
      />

      <NovedadModal
        visible={novedadModalVisible}
        onCancel={() => {
          setNovedadModalVisible(false);
        }}
        onConfirm={novedadText => {
          finalizeLiquidacion(diferencia, novedadText);
        }}
        diferencia={diferencia}
      />

      <FacturaDetailsModal
        visible={isFacturaDetailsVisible}
        factura={selectedFactura}
        onClose={() => {
          setIsFacturaDetailsVisible(false);
          setSelectedFactura(null);
        }}
      />
    </Layouts>
  );
}

export default LiquidacionesVendedor;
